/**
 * Display helpers for users whose account has been deleted.
 *
 * Deletion pseudonymises the row: the name goes, and the address becomes
 * `deleted+<32 hex user id>@deleted.invalid`. Nothing in the API marks the
 * row as deleted, so the address is the signal. These helpers keep the raw
 * tombstone out of the UI and replace it with a short, consistent label.
 */

const TOMBSTONE_PATTERN = /^deleted\+([0-9a-f]{32})@deleted\.invalid$/i;

export const DELETED_USER_LABEL = "Deleted user";

export interface DisplayableUser {
  fullName?: string | null;
  email?: string | null;
}

export const isDeletedUserEmail = (email?: string | null): boolean =>
  !!email && TOMBSTONE_PATTERN.test(email.trim());

/** Short, recognisable form of a tombstone address: `deleted+01a15569…`. */
export const shortDeletedUserEmail = (email: string): string => {
  const match = TOMBSTONE_PATTERN.exec(email.trim());
  return match ? `deleted+${match[1].slice(0, 8)}…` : email;
};

export interface DisplayUserNameOptions {
  /**
   * Fall back to the address when the user has no name (default). Pass false
   * where the address is shown alongside, such as a table with an Email column.
   */
  useEmail?: boolean;
}

/**
 * Name to show for a user: their name, else (optionally) their address, else
 * `fallback`; a deleted user always shows as "Deleted user".
 */
export const displayUserName = (
  user?: DisplayableUser | null,
  fallback = "Unknown",
  { useEmail = true }: DisplayUserNameOptions = {}
): string => {
  if (!user) {
    return fallback;
  }
  if (isDeletedUserEmail(user.email)) {
    return DELETED_USER_LABEL;
  }
  const name = user.fullName?.trim();
  if (name) {
    return name;
  }
  return (useEmail && user.email?.trim()) || fallback;
};

/** Address to show for a user; a tombstone is shortened. */
export const displayUserEmail = (email?: string | null): string | null => {
  if (!email) {
    return email ?? null;
  }
  return isDeletedUserEmail(email) ? shortDeletedUserEmail(email) : email;
};
